import { useState } from 'react'
import { makeOnce } from '../../utils/makeOnce'
import { ResourceMap } from '../manifest'
import { registerResource, unregisterResource } from '../storage'
import { Entity, GetResourceMutations, ResourceType } from '../types'
import { logger } from '../../utils/logging'

// todo: dedupe this logic
export function useCreateResource<K extends ResourceType>(resourceType: K) {
  const [isCreating, setCreating] = useState(false)

  const mutator = makeOnce(
    ResourceMap[resourceType].create,
    async (...args: Parameters<GetResourceMutations<K, 'create'>>) => {
      setCreating(true)

      const response = await ResourceMap[resourceType].create(args[0]).finally(() => {
        setCreating(false)
      })

      if (!response || !('data' in response)) {
        throw new Error('Invalid response')
      }

      registerResource(response.data)
      return response.data as Entity<K>
    }
  )
  return [mutator, isCreating] as const
}

export function useUpdateResource<K extends ResourceType>(resourceType: K) {
  const [inProgress, setInProgress] = useState(false)

  const mutator = makeOnce(
    ResourceMap[resourceType].update,
    async (...args: Parameters<GetResourceMutations<K, 'update'>>) => {
      setInProgress(true)

      const response = await ResourceMap[resourceType].update(args[0], args[1]).finally(() => {
        setInProgress(false)
      })

      if (!response || !('data' in response)) {
        throw new Error('Invalid response')
      }

      registerResource(response.data)
      return response.data as Entity<K>
    }
  )
  return [mutator, inProgress] as const
}

export function useUpgradeResource<K extends ResourceType>(resourceType: K) {
  const [inProgress, setInProgress] = useState(false)

  const mutator = makeOnce(
    ResourceMap[resourceType].upgrade,
    async (...args: Parameters<GetResourceMutations<K, 'upgrade'>>) => {
      setInProgress(true)

      const response = await ResourceMap[resourceType].upgrade(args[0], args[1]).finally(() => {
        setInProgress(false)
      })

      if (!response || !('data' in response)) {
        throw new Error('Invalid response')
      }

      registerResource(response.data)
      return response.data as Entity<K>
    }
  )
  return [mutator, inProgress] as const
}

export function useDeleteResource<K extends ResourceType>(resourceType: K) {
  const [isDeleting, setDeleting] = useState(false)

  const mutator = makeOnce(
    ResourceMap[resourceType].delete,
    async (...args: Parameters<GetResourceMutations<K, 'delete'>>) => {
      setDeleting(true)

      const response = await ResourceMap[resourceType].delete(args[0]).finally(() => {
        unregisterResource(args[0])
        setDeleting(false)
      })

      if (!response || !response.data.success) {
        setDeleting(false)
        logger.error('Failed to delete resource', response.data)
        throw new Error('Failed to delete resource')
      }
    }
  )

  return [mutator, isDeleting] as const
}

export function useBulkDeleteResource<K extends ResourceType>(resourceType: K) {
  const [isDeleting, setDeleting] = useState(false)

  const mutator = makeOnce(
    ResourceMap[resourceType].bulkDelete,
    async (...args: Parameters<GetResourceMutations<K, 'bulkDelete'>>) => {
      setDeleting(true)

      try {
        const response: any = await ResourceMap[resourceType].bulkDelete(args[0]).finally(() => {
          const deletedIds = Object.values(Object.keys(args[0])[0])
          deletedIds.forEach((id: string) => {
            unregisterResource(id)
          })
          setDeleting(false)
        })

        if (!response || !response.data?.success) {
          setDeleting(false)
          logger.error('Failed to delete resource', response.data)
          throw new Error('Failed to delete resource')
        }
        return await Promise.resolve(response)
      } catch (err: any) {
        logger.error('Failed to delete resource', err)
        throw new Error('Failed to delete resource')
      }
    }
  )

  return [mutator, isDeleting] as const
}

export function useBulkUpdateResource<K extends ResourceType>(resourceType: K) {
  const [isUpdating, setUpdating] = useState(false)

  const mutator = makeOnce(
    ResourceMap[resourceType].bulkUpdate,
    async (...args: Parameters<GetResourceMutations<K, 'bulkUpdate'>>) => {
      setUpdating(true)

      const response: any = await ResourceMap[resourceType].bulkUpdate(args[0]).finally(() => {
        setUpdating(false)
      })

      return Promise.resolve(response)
    }
  )

  return [mutator, isUpdating] as const
}

export function useArchiveResource<K extends ResourceType>(resourceType: K) {
  const [inProgress, setInProgress] = useState(false)

  const mutator = makeOnce(
    ResourceMap[resourceType].archive,
    async (...args: Parameters<GetResourceMutations<K, 'archive'>>) => {
      setInProgress(true)

      const response = await ResourceMap[resourceType].archive(args[0], args[1]).finally(() => {
        setInProgress(false)
      })

      if (!response || !('data' in response)) {
        throw new Error('Invalid response')
      }

      registerResource(response.data)
      return response.data as Entity<K>
    }
  )
  return [mutator, inProgress] as const
}
